import type { ITradingCompetitionTrader } from "@/sanity/pages/PageTradingCompetitionTemplate";
import type { BoardRow, BoardSource } from "./board";

/**
 * DUMMY standings for the `?test-leaderboard` preview, until the real feed exists. Deterministic
 * (seeded), so every reload shows the same board. The top ten are the CMS sample traders, so the
 * podium matches the design.
 *
 * The real source must look emails up on the server: participants' emails never ship to the browser.
 */

const PARTICIPANTS = 1248;
const TOP = 100;

const FIRST = [
  "Lukas", "Anna", "Jonas", "Sophie", "Felix", "Emma", "Max", "Lena", "Paul", "Marie", "David", "Sarah",
  "Niklas", "Laura", "Tim", "Hannah", "Jan", "Lea", "Moritz", "Clara", "Simon", "Nina", "Daniel", "Lisa",
  "Erik", "Amelie", "Marco", "Sofia", "Ahmed", "Yusuf", "Luca", "Elias", "Noah", "Ben", "Finn", "Mila",
  "Ida", "Chiara", "Oliver", "James", "Kerem", "Mats", "Ella", "Nora", "Theo", "Jakob", "Selin", "Milan",
];
const INITIALS = "ABCDEFGHJKLMNOPRSTUVWZ";

/** Test emails for the finder and the rank each one lands on. */
const TEST_EMAILS: Record<string, number> = {
  "podium@example.com": 2,
  "prize@example.com": 37,
  "first-out@example.com": 101,
  "outside@example.com": 214,
  "negative@example.com": PARTICIPANTS - 3,
};

function mulberry32(seed: number) {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Ranks 11+: a steep head, a long tail around zero, and the last ~28% below zero. */
function tailScore(rank: number, rand: () => number) {
  const x = (rank - 10) / (PARTICIPANTS - 10);
  const base = 30500 * (1 - x) ** 40 + 900 * (1 - x) ** 2 - 2600 * Math.max(0, (x - 0.72) / 0.28);
  return Math.round(base * (0.97 + rand() * 0.06));
}

function build(traders: ITradingCompetitionTrader[]) {
  const rand = mulberry32(20261019);
  const rows: Omit<BoardRow, "rank">[] = traders
    .slice(0, 10)
    .map((t, i) => ({ id: `cms-${i}`, name: t.name ?? "", total: t.points ?? 0, lastDay: t.lastDay ?? 0 }));
  for (let r = rows.length + 1; r <= PARTICIPANTS; r++) {
    const total = tailScore(r, rand);
    const name = `${FIRST[Math.floor(rand() * FIRST.length)]} ${INITIALS[Math.floor(rand() * INITIALS.length)]}.`;
    const swing = 120 + Math.abs(total) * 0.04;
    const lastDay = Math.round((rand() - (total < 0 ? 0.7 : 0.35)) * swing * 2);
    rows.push({ id: `t-${r}`, name, total, lastDay });
  }
  return rows.sort((a, b) => b.total - a.total).map((row, i) => ({ ...row, rank: i + 1 }));
}

let cache: { key: string; ranked: BoardRow[] } | undefined;

export function createDummyBoard(traders: ITradingCompetitionTrader[]): BoardSource {
  const key = JSON.stringify(traders.slice(0, 10).map((t) => [t.name, t.points, t.lastDay]));
  if (cache?.key !== key) cache = { key, ranked: build(traders) };
  const { ranked } = cache;

  return {
    top: ranked.slice(0, TOP),
    async lookup(email) {
      await new Promise((resolve) => setTimeout(resolve, 450)); // a network round trip
      const rank = TEST_EMAILS[email.trim().toLowerCase()];
      if (!rank) return null;
      return { ...ranked[rank - 1], ahead: ranked[rank - 2]?.total, behind: ranked[rank]?.total };
    },
  };
}
